import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const ThemeCtx = createContext(null);

const VALID_THEMES = ["red", "blue", "green", "amber", "purple", "crimson", "midnight", "copper", "neon", "carbon", "logored", "scarlet", "gold", "gunmetal", "ember"];

const applyThemeColor = (themeId) => {
  const root = document.documentElement;
  VALID_THEMES.forEach((t) => root.classList.remove(`theme-${t}`));
  if (themeId && themeId !== "red") root.classList.add(`theme-${themeId}`);
};

export const ThemeProvider = ({ children }) => {
  // Digital Twin Studio redesign: site is dark-first, always
  const [mode, setMode] = useState("dark");
  const [manual, setManual] = useState(() => localStorage.getItem("ta_theme_manual") === "1");
  const [themeColor, setThemeColor] = useState(() => localStorage.getItem("ta_theme_color") || "red");
  const [siteSettings, setSiteSettings] = useState(null);

  const refreshSettings = useCallback(async () => {
    try {
      const r = await api.get("/settings");
      setSiteSettings(r.data || null);
      const tid = r.data?.theme_id;
      if (tid && VALID_THEMES.includes(tid)) {
        setThemeColor(tid);
        localStorage.setItem("ta_theme_color", tid);
      }
    } catch (err) {
      console.warn("ThemeContext: settings fetch failed — falling back to defaults", err);
    }
  }, []);

  // Fetch site-wide theme + settings from backend on mount (admin-controlled)
  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  // Apply light/dark class
  useEffect(() => {
    const root = document.documentElement;
    if (mode === "light") {
      root.classList.add("light"); root.classList.remove("dark");
    } else {
      root.classList.add("dark"); root.classList.remove("light");
    }
    root.setAttribute("data-theme", mode);
  }, [mode]);

  // Apply color theme class
  useEffect(() => {
    applyThemeColor(themeColor);
  }, [themeColor]);



  const toggle = useCallback(() => {
    setMode((m) => (m === "dark" ? "light" : "dark"));
    setManual(true);
    localStorage.setItem("ta_theme_manual", "1");
  }, []);

  const setColorTheme = useCallback((id) => {
    if (!VALID_THEMES.includes(id)) return;
    setThemeColor(id);
    localStorage.setItem("ta_theme_color", id);
  }, []);

  useEffect(() => {
    if (manual) localStorage.setItem("ta_theme", mode);
  }, [mode, manual]);

  const value = useMemo(
    () => ({ mode, manual, toggle, themeColor, setColorTheme, themes: VALID_THEMES, siteSettings, refreshSettings }),
    [mode, manual, toggle, themeColor, setColorTheme, siteSettings, refreshSettings]
  );

  return (
    <ThemeCtx.Provider value={value}>
      {children}
    </ThemeCtx.Provider>
  );
};

export const useTheme = () => useContext(ThemeCtx);
